/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-nested-ternary */
import { FC, useEffect, useMemo, useState, ChangeEvent } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from './EditPriceDrawer.module.css';
import { t } from 'i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import { useUpdateProductPrice } from '@/services/modifiers/modifiers.service';
import { useSTKContext } from '@/context/context';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { getStores } from '@/services/modifiers/modifiers.helper.service';
import { isPriceInvalid } from '@/services/menu/menus.helper.service';
import { InputStates } from '@/components/Input/Input.types';
import { Typography } from '@mui/material';
import Button from '@/components/Button/Button';
import { Modifier, VariantModifierPriceUpdateRequest } from '@/types/menus.types';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import { EditPriceModifierDrawerProps } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceModifierDrawerProps.types';
import ModifiersTable from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceTabItems/ModifiersTable';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import { ModifierPriceChangeConfirmation } from '@/pages/Franchisee/Menus/EditPriceDrawer/ModifierPriceChangeConfirmation';
import {
  areAllModifierStoreGroupPricesEqualToTarget,
  getStoreGroupNamesWithExistingPrices,
  getStoresWithExistingPricesForModifier,
  isModifierCategoryPriceUpdateOnly,
  isZeroPricedModifierSelection,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import { getSelectedStoreGroupNames } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierSelection.helpers';

function sanitizePriceInput(value: string): string {
  // Keep digits and a single dot; cap to 2 decimal places. This prevents stray characters
  // from being sent to the API (e.g. "$", spaces, commas).
  const raw = String(value ?? '').trim();
  if (raw === '') return '';
  const onlyDigitsAndDot = raw.replace(/[^\d.]/g, '');
  if (onlyDigitsAndDot === '') return '';

  const [intPartRaw, ...rest] = onlyDigitsAndDot.split('.');
  const intPart = intPartRaw ?? '';
  const decPart = rest.join('').slice(0, 2); // collapse extra dots, keep first 2 decimals

  return decPart.length ? `${intPart}.${decPart}` : intPart;
}

function buildModifierPriceUpdateRequests(
  modifiers: Modifier[],
  inputPrice: string,
  categoryName: string,
  selectedStores: string[],
  options?: { priceUpdateOnly?: boolean; selectedStoreGroupNames?: string[] },
): VariantModifierPriceUpdateRequest[] {
  const sanitizedPrice = sanitizePriceInput(inputPrice);
  const { priceUpdateOnly = false, selectedStoreGroupNames = [] } = options ?? {};

  return modifiers
    .filter((modifier) => {
      if (!modifier.variantId) return false;

      if (!modifier.priceIsLocked) return true;

      const hasActivePrice = modifier.storeGroups?.some((storeGroup) => {
        const priceRange = storeGroup.chosenPriceRange;
        return priceRange && priceRange !== 'Inactive' && priceRange.trim() !== '';
      });

      return !hasActivePrice;
    })
    .map((modifier) => {
      const stores =
        priceUpdateOnly && selectedStoreGroupNames.length > 0
          ? getStoresWithExistingPricesForModifier(modifier, selectedStoreGroupNames, selectedStores)
          : selectedStores;

      return {
        stores,
        modifierKeys: {
          variantId: modifier.variantId as string,
          slotId: modifier.slotId,
          modifierId: modifier.modifierId,
          modifierWeightId: modifier.modifierWeightId,
        },
        price: sanitizedPrice,
        modifierCategory: categoryName,
      };
    });
}

function getUniqueStoreCountFromModifierPriceUpdateRequests(requests: VariantModifierPriceUpdateRequest[]): number {
  const uniqueStores = new Set<string>();
  requests.forEach((request) => {
    request.stores?.forEach((store) => uniqueStores.add(store));
  });
  return uniqueStores.size;
}

export const EditPriceModifierDrawer: FC<EditPriceModifierDrawerProps> = ({
  isOpen,
  onClose,
  storeModifiers,
  afterSave,
  setPriceDeviationsData,
  categoryName,
}) => {
  const { storesAndGroups } = useSTKContext();
  const [state, setState] = useState(isOpen);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [inputPriceState, setInputPriceState] = useState(InputStates.Default);
  const [errorResponse, setErrorResponse] = useState<any | undefined>(undefined);
  const stores = getStores(storesAndGroups);
  const {
    mutateAsync: updateProductPrice,
    data: updatePriceResponse,
    isLoading: isSavingPrice = false,
  } = useUpdateProductPrice();

  const [inputPrice, setInputPrice] = useState('');
  const [isPriceChangeConfirmationOpen, setIsPriceChangeConfirmationOpen] = useState<boolean>(false);

  const currentModifiers = storeModifiers;
  const isZeroPricedSelection = useMemo(() => isZeroPricedModifierSelection(currentModifiers), [currentModifiers]);
  const isPriceUpdateOnlyCategory = isModifierCategoryPriceUpdateOnly(categoryName);

  /** Modifier upcharges are always validated against $0.00–$5.00 (not store-group ranges). */
  const lowestValidPrice = '0.00';
  const highestValidPrice = isZeroPricedSelection ? '0.00' : '5.00';

  useEffect(() => {
    if (!isOpen) {
      setInputPrice('');
      setErrorResponse(undefined);
    } else if (isZeroPricedSelection) {
      setInputPrice('0.00');
    }
    setState(isOpen);
  }, [isOpen, isZeroPricedSelection]);

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isZeroPricedSelection) {
      return;
    }

    const { value } = e.target;
    setInputPrice(value);
  };

  const isInputPriceValid = useMemo(() => {
    if (isZeroPricedSelection) {
      return sanitizePriceInput(inputPrice) === '0.00' || sanitizePriceInput(inputPrice) === '0';
    }

    return !isPriceInvalid(inputPrice, lowestValidPrice, highestValidPrice);
  }, [highestValidPrice, inputPrice, isZeroPricedSelection, lowestValidPrice]);

  const selectedStoreGroupNames = useMemo(() => getSelectedStoreGroupNames(storesAndGroups), [storesAndGroups]);

  const storeGroupNamesForPriceComparison = useMemo(() => {
    if (isPriceUpdateOnlyCategory) {
      return getStoreGroupNamesWithExistingPrices(currentModifiers, selectedStoreGroupNames);
    }

    return selectedStoreGroupNames;
  }, [currentModifiers, isPriceUpdateOnlyCategory, selectedStoreGroupNames]);

  const isNewPriceSameAsAllStoreGroups = useMemo(() => {
    if (!inputPrice || !isInputPriceValid) {
      return false;
    }

    return areAllModifierStoreGroupPricesEqualToTarget(
      currentModifiers,
      storeGroupNamesForPriceComparison,
      sanitizePriceInput(inputPrice),
      { priceUpdateOnly: isPriceUpdateOnlyCategory },
    );
  }, [currentModifiers, inputPrice, isInputPriceValid, isPriceUpdateOnlyCategory, storeGroupNamesForPriceComparison]);

  const submissionStoreCount = useMemo(() => {
    const requests = buildModifierPriceUpdateRequests(
      currentModifiers,
      sanitizePriceInput(inputPrice),
      categoryName,
      stores,
      {
        priceUpdateOnly: isPriceUpdateOnlyCategory,
        selectedStoreGroupNames,
      },
    );

    return getUniqueStoreCountFromModifierPriceUpdateRequests(requests);
  }, [categoryName, currentModifiers, inputPrice, isPriceUpdateOnlyCategory, selectedStoreGroupNames, stores]);

  const processModifier = () => {
    if (!isInputPriceValid) {
      setInputPriceState(InputStates.Error);
    } else {
      const variantModifierPriceUpdateRequest = buildModifierPriceUpdateRequests(
        storeModifiers,
        inputPrice,
        categoryName,
        stores,
        {
          priceUpdateOnly: isPriceUpdateOnlyCategory,
          selectedStoreGroupNames,
        },
      );

      updateProductPrice(variantModifierPriceUpdateRequest, {
        onSuccess: () => {
          setPriceIsSaved(true);
          afterSave();
        },
        onError: (error: any) => {
          if (
            error?.response?.status === 504 ||
            error?.code === 'ECONNABORTED' ||
            error?.message?.includes('timeout') ||
            error?.message?.includes('Gateway Timeout')
          ) {
            const timeoutResponse = {
              failedStores: [],
              status: 'TIMEOUT',
              message: t('requestProcessingModal.timeout.message'),
            };
            setErrorResponse(timeoutResponse);
            setPriceIsSaved(true);
            afterSave();
            return;
          }
          const errorResponseObj = {
            failedStores: [],
            status: 'ERROR',
            message: error?.message || t('editMenuPrice.errorMessage'),
          };
          setErrorResponse(errorResponseObj);
          setPriceIsSaved(true);
          afterSave();
        },
      });
    }
  };

  const onCloseDrawer = () => () => {
    setPriceIsSaved(false);
    setInputPriceState(InputStates.Default);
    setInputPrice('');
    setErrorResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer()} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer()}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.alertBanner}>
            <CalendarAlert />
          </div>
          <div className={styles.content}>
            {isSavingPrice ? (
              <EditDrawerProcessing count={submissionStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : priceIsSaved ? (
              <EditDrawerSuccessAndFailure
                apiResponse={errorResponse || updatePriceResponse}
                entityToUpdate={EntityToUpdate.MENU}
              />
            ) : (
              <>
                <div className={styles.productSettings}>
                  <strong>{t('editMenuPrice.editMenuPrice')}</strong>
                  {isZeroPricedSelection ? (
                    <p className={styles.zeroPricedNotice}>{t('editMenuPrice.zeroPricedModifierInfo')}</p>
                  ) : isPriceUpdateOnlyCategory ? (
                    <p className={styles.zeroPricedNotice}>{t('editMenuPrice.modifierPriceUpdateOnlyInfo')}</p>
                  ) : (
                    <p className={styles.subtext1}>{t('editMenuPrice.activateInactiveProducts')}</p>
                  )}
                  <MaskedInput
                    className={styles.input}
                    id="new-price"
                    label={t('editMenuPrice.newPricePoint')}
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    value={inputPrice}
                    onChange={handlePriceChange}
                    state={inputPriceState}
                    disabled={isZeroPricedSelection}
                  />
                  <Typography
                    mt={'2px'}
                    mb={'10px'}
                    sx={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: `${inputPriceState === InputStates.Error ? 'var(--red-700)' : 'var(--black-700)'}`,
                    }}
                  >
                    {isZeroPricedSelection
                      ? `${t('editMenuPrice.zeroPricedRequiredPrice')}: $0.00`
                      : `${t('editMenuPrice.priceRange')}: $${lowestValidPrice} - $${highestValidPrice}`}
                  </Typography>
                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.submitButton}
                      data-testid="edit-price"
                      disabled={
                        currentModifiers.length === 0 ||
                        inputPrice === '' ||
                        !isInputPriceValid ||
                        isNewPriceSameAsAllStoreGroups
                      }
                      onClick={() => setIsPriceChangeConfirmationOpen(true)}
                      type="button"
                    >
                      {t('editMenuPrice.continue')}
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <ModifiersTable
                    storeModifiers={storeModifiers}
                    modifierCategory={categoryName}
                    setPriceDeviationsData={setPriceDeviationsData}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
      <ModifierPriceChangeConfirmation
        isOpen={isPriceChangeConfirmationOpen}
        onClose={() => setIsPriceChangeConfirmationOpen(false)}
        onCancel={onCloseDrawer()}
        storeModifiers={storeModifiers}
        afterSave={processModifier}
        newPrice={sanitizePriceInput(inputPrice)}
        categoryName={categoryName}
      />
    </div>
  );
};
